// OrderDetailView — full order view inside the account shell: top back link,
// fulfillment stepper, Bill to / Ship to snapshot addresses (F5), items with
// thumbnails, totals, payment status.
//
// Consumes the REAL GET /me/orders/{id} envelope { order, lines, payments }
// (TD-225 / §39). The Go model is the source of truth: there is no timeline
// and no created_at (§9) to render — those phantom sections stay removed.
//
// ⚠️ AMENDED 2026-10-03 (redesign Task 5, operator rulings F5/F24/S5/A — the
// ORIGINAL text is preserved below):
//   - The shipping-address block NO LONGER resolves order.shipping_address_id
//     against listAddresses(): that lookup read the customer's MUTABLE saved
//     addresses, so an edited address book silently rewrote what a placed
//     order claimed it shipped to. The detail now renders the order's OWN
//     frozen snapshots (bill_to / ship_to, migration 124 / Task 14). A NULL
//     snapshot (a pre-124 order) renders "Not recorded"; an ABSENT key (a
//     pre-API-4 server) omits the block (D5). The
//     SHIPPING_ADDRESS_VISIBLE_STATUSES gate is removed — the addresses show
//     for every status (F5), including "confirmed", the exact state the old
//     gate hid.
//   - F24: the masked payment summary (order.payment.label) renders under
//     Bill to.
//   - Badges read the ONE status map (embed-shared/status.ts via
//     EmbedStatusBadge) — the local statusColors/statusPayment maps and the
//     "Payment: paid" prefix are retired with order-status-badge.tsx.
//   - Items render with pictures (ruling S5=A: the wire carries the product's
//     current image_url): a line with an image renders it (alt = product
//     name); a line without renders a LABELLED placeholder tile. This
//     reverses TD-224's "images are not a data concept" pin.
//   - The bottom "Back to Orders" button is replaced by a TOP "← Back to
//     orders" link as the view's first control (the shell's account nav sits
//     above this view; the control lives here, not in AccountOrderDetail, so
//     the composed view exposes exactly one).
//   - The placed-at date line renders when the wire carries placed_at
//     (viewer-local via formatInstantDate).
//
// ORIGINAL (2026-06, superseded in part above): "the shipping-address block
// resolves the real `order.shipping_address_id` FK against the customer's
// saved addresses (listAddresses()) — still no denormalized address object
// on the order wire itself, just an honest lookup of a real reference."
//
// KEPT: TD-225 envelope + payment-status derivation from payments[] (no
// phantom payment_status field), the FulfillmentStepper (cancelled banner
// instead of a stepper), totals incl. the TD-308 operator-configurable
// zero-shipping label, the promo/discount row.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens().

import {
  useEmbedTokens,
  EmbedText,
  EmbedLink,
  EmbedStatusBadge,
} from "../../embed-shared/primitives";
import { formatCurrency } from "../../embed-shared/currency";
import { TAX_AT_BILLING_VALUE, TOTAL_BEFORE_TAX_LABEL, isTaxPending } from "../../embed-shared/tax-display";
import { formatInstantDate } from "../../embed-shared/format";
import type { OrderDetail, OrderAddressSnapshot } from "../../embed-shared/types";
import { useStorefrontConfig } from "./storefront-config";
import { FulfillmentStepper } from "./FulfillmentStepper";
import { ItemThumb } from "./ItemThumb";

export interface OrderDetailViewProps {
  detail: OrderDetail;
  onBack?: () => void;
}

function SectionHeading({ children }: { children: string }) {
  const t = useEmbedTokens();
  return (
    <h4
      style={{
        fontSize: t.fontSize.sm,
        fontWeight: 600,
        color: t.colors.textSecondary,
        margin: `0 0 ${t.spacing.sm}px 0`,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </h4>
  );
}

/**
 * One address block (F5). `null` snapshot = the order predates migration 124:
 * the block still renders, as "Not recorded" (ruling S5). An undefined key
 * (pre-API-4 server) never reaches here — the caller omits the whole block.
 */
function AddressBlock({ heading, address }: { heading: string; address: OrderAddressSnapshot | null }) {
  return (
    <div style={{ flex: "1 1 260px", minWidth: 0 }}>
      <SectionHeading>{heading}</SectionHeading>
      {address === null ? (
        <EmbedText tone="secondary">Not recorded</EmbedText>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <EmbedText>
            {address.line1}
            {address.line2 ? `, ${address.line2}` : ""}
          </EmbedText>
          <EmbedText tone="secondary">
            {address.city}, {address.state} {address.postal_code}
          </EmbedText>
        </div>
      )}
    </div>
  );
}

export function OrderDetailView({ detail, onBack }: OrderDetailViewProps) {
  const t = useEmbedTokens();
  // TD-308: shipping=$0 label is operator-configurable (default "$0.00" since
  // migration 144). D6: the $0 value renders in the neutral text token, not
  // the success green — a green $0.00 reads as a promotion.
  const { freeShippingLabel } = useStorefrontConfig();
  const { order, lines, payments } = detail;
  // Payment status is real data from the payments[] array, not a phantom
  // order field. No payment row yet ⇒ pending.
  const paymentStatus = payments.length > 0 ? payments[0].status : "pending";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.lg }}>
      {/* Top back control — the FIRST element (the shell's nav sits above);
          the old bottom "Back to Orders" button is retired. */}
      {onBack && (
        <div style={{ alignSelf: "flex-start" }}>
          <EmbedLink onClick={onBack}>← Back to orders</EmbedLink>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: t.spacing.sm }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <h3
            style={{
              fontSize: t.fontSize.lg,
              fontWeight: 600,
              color: t.colors.text,
              margin: 0,
            }}
          >
            Order #{order.order_number}
          </h3>
          {order.placed_at && (
            <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
              {/* "Order placed" is the label span; the date is its own
                  element so D5 can drop the whole line with the field. */}
              <span>Order placed</span> <span>{formatInstantDate(order.placed_at, { withYear: true })}</span>
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: t.spacing.sm }}>
          <EmbedStatusBadge domain="order" value={order.status} />
          <EmbedStatusBadge domain="payment" value={paymentStatus} />
        </div>
      </div>

      {/* Fulfillment progress (T6, design.md AC4) — derived purely from
          order.status; see FulfillmentStepper.tsx for the cancelled/refunded
          banner-vs-stepper reasoning. */}
      <FulfillmentStepper status={order.status} />

      {/* Bill to / Ship to (F5) — the order's own frozen snapshots, side by
          side, stacking at phone width (flexWrap). Under Bill to: the masked
          payment summary (F24). Block-scoped D5: an absent key omits the
          block; a null one renders "Not recorded" inside it. */}
      {(order.bill_to !== undefined || order.ship_to !== undefined) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: t.spacing.lg }}>
          {order.bill_to !== undefined && (
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <AddressBlock heading="Bill to" address={order.bill_to} />
              {order.payment && (
                <div style={{ marginTop: t.spacing.xs }}>
                  <EmbedText tone="secondary">{order.payment.label}</EmbedText>
                </div>
              )}
            </div>
          )}
          {order.ship_to !== undefined && <AddressBlock heading="Ship to" address={order.ship_to} />}
        </div>
      )}

      {/* Items — cart-line layout with thumbnails (F5). */}
      <div>
        <SectionHeading>Items</SectionHeading>
        {lines.map((line) => (
          <div
            key={line.id}
            style={{
              display: "flex",
              gap: t.spacing.md,
              padding: `${t.spacing.sm}px 0`,
              borderBottom: `1px solid ${t.colors.cardBorder}`,
              alignItems: "center",
            }}
          >
            <ItemThumb name={line.product_name} imageUrl={line.image_url ?? null} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: t.fontSize.sm, fontWeight: 500, color: t.colors.text, overflowWrap: "anywhere" }}>
                {line.product_name}
              </span>
              <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary, marginLeft: t.spacing.sm }}>
                x{line.quantity}
              </span>
            </div>
            <span style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>
              {formatCurrency(line.line_total)}
            </span>
          </div>
        ))}
      </div>

      {/* Totals — mock .summary-row: regular rows read as secondary text with
          tabular values; the total row alone carries primary bold text. */}
      <div style={{ borderTop: `1px solid ${t.colors.cardBorder}`, paddingTop: t.spacing.sm }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0` }}>
          <EmbedText tone="secondary">Subtotal</EmbedText>
          <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, fontVariantNumeric: "tabular-nums", lineHeight: t.lineHeight.normal }}>{formatCurrency(order.subtotal)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0` }}>
          <EmbedText tone="secondary">Tax</EmbedText>
          <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, fontVariantNumeric: "tabular-nums", lineHeight: t.lineHeight.normal }}>{isTaxPending(order.tax_amount) ? TAX_AT_BILLING_VALUE : formatCurrency(order.tax_amount)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0` }}>
          <EmbedText tone="secondary">Shipping</EmbedText>
          {order.shipping === 0 ? (
            <span style={{ color: t.colors.text, fontWeight: 600, fontSize: t.fontSize.sm, fontVariantNumeric: "tabular-nums" }}>{freeShippingLabel}</span>
          ) : (
            <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary, fontVariantNumeric: "tabular-nums", lineHeight: t.lineHeight.normal }}>{formatCurrency(order.shipping)}</span>
          )}
        </div>
        {order.discount_amount > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: `${t.spacing.xs}px 0`, color: t.colors.successGreen }}>
            <span style={{ fontSize: t.fontSize.sm, color: t.colors.successGreen }}>
              {order.promo_code ? `Promo (${order.promo_code})` : "Discount"}
            </span>
            <span style={{ fontSize: t.fontSize.sm, color: t.colors.successGreen, fontVariantNumeric: "tabular-nums" }}>
              -{formatCurrency(order.discount_amount)}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text, padding: `${t.spacing.sm}px 0`, borderTop: `1px solid ${t.colors.cardBorder}`, marginTop: t.spacing.xs }}>
          <span>{isTaxPending(order.tax_amount) ? TOTAL_BEFORE_TAX_LABEL : "Total"}</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(order.total)}</span>
        </div>
      </div>
    </div>
  );
}
